import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { articles } from '@/mocks/articles';

const guideSteps = [
  {
    step: "01",
    title: "Wybierz kategorię",
    desc: "Rozpocznij od wyboru interesującej Cię kategorii produktów w Kakobuy Spreadsheet. Kategorie są podzielone tematycznie – od butów po akcesoria.",
    icon: "ri-grid-line",
  },
  {
    step: "02",
    title: "Przeglądaj produkty",
    desc: "Przejrzyj dostępne produkty w wybranej kategorii. Każdy wpis zawiera szczegółowe informacje, oceny jakości i linki do sprzedawców.",
    icon: "ri-eye-line",
  },
  {
    step: "03",
    title: "Porównaj opcje",
    desc: "Zestaw ze sobą różne produkty, porównaj ceny i jakość. Kakobuy Spreadsheet ułatwia szybkie podejmowanie świadomych decyzji.",
    icon: "ri-scales-line",
  },
  {
    step: "04",
    title: "Sprawdź sprzedawcę",
    desc: "Zweryfikuj sprzedawcę przed zakupem. Spreadsheet zawiera informacje o ocenach, historii i wiarygodności każdego sprzedawcy.",
    icon: "ri-shield-check-line",
  },
  {
    step: "05",
    title: "Złóż zamówienie",
    desc: "Użyj linków z arkusza, aby przejść bezpośrednio do oferty na Kakobuy. Zamów produkt i śledź status swojego zamówienia.",
    icon: "ri-shopping-cart-line",
  },
  {
    step: "06",
    title: "Odbierz i oceń",
    desc: "Po otrzymaniu produktu sprawdź jego jakość i podziel się opinią, aby pomóc innym użytkownikom w podejmowaniu decyzji.",
    icon: "ri-star-line",
  },
];

const tips = [
  {
    title: "Zawsze sprawdzaj zdjęcia QC",
    desc: "Przed zatwierdzeniem wysyłki poproś o zdjęcia kontrolne Quality Check. Pozwoli Ci to ocenić rzeczywisty stan produktu.",
  },
  {
    title: "Łącz zamówienia dla oszczędności",
    desc: "Grupuj kilka produktów w jednym zamówieniu, aby zaoszczędzić na kosztach wysyłki międzynarodowej.",
  },
  {
    title: "Monitoruj sezonowe promocje",
    desc: "Kakobuy często oferuje promocje sezonowe i zniżki na wysyłkę. Warto śledzić te okazje.",
  },
  {
    title: "Korzystaj ze społeczności",
    desc: "Dołącz do grup i forów użytkowników Kakobuy. Społeczność chętnie dzieli się doświadczeniami i poleca zaufanych sprzedawców.",
  },
];

export default function PoradnikPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://kakobuypolska.rest';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const pageUrl = `${siteUrl}/poradnik`;

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    url: pageUrl,
    name: 'Poradnik zakupowy Kakobuy – jak kupować krok po kroku',
    description: 'Kompletny przewodnik po zakupach na Kakobuy. Od wyboru produktu po bezpieczną dostawę do Polski. Praktyczne porady i strategie zakupowe.',
    inLanguage: 'pl',
    isPartOf: {
      '@type': 'WebSite',
      name: 'Kakobuy Polska Spreadsheet',
      url: siteUrl,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Strona główna', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Poradnik zakupowy', item: pageUrl },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <main className="pt-16">
        <div className="relative w-full h-[350px] md:h-[420px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Editorial%20photography%20of%20a%20warm%20minimalist%20desk%20setup%20with%20an%20open%20notebook%20showing%20handwritten%20shopping%20guide%20notes%2C%20a%20cup%20of%20coffee%2C%20a%20tablet%20displaying%20colorful%20product%20catalog%2C%20soft%20natural%20daylight%20from%20window%2C%20cream%20and%20terracotta%20tones%20with%20sage%20green%20accents%2C%20lifestyle%20editorial%20style%2C%20cozy%20and%20organized%20atmosphere%2C%20storytelling%20composition&width=1800&height=900&seq=guide-hero-01&orientation=landscape"
            alt="Poradnik zakupowy Kakobuy"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-background-50"></div>

          <div className="absolute bottom-0 left-0 right-0 z-10 pb-10">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="max-w-[700px]">
                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
                  Poradnik zakupowy Kakobuy – jak kupować krok po kroku
                </h1>
                <p className="text-base md:text-lg text-white/90 leading-relaxed">
                  Kompletny przewodnik po zakupach na Kakobuy. Od wyboru produktu po bezpieczną dostawę do Polski.
                </p>
              </div>
            </div>
          </div>
        </div>

        <section className="w-full py-10 md:py-16 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-3">
                Jak korzystać z Kakobuy – 6 prostych kroków
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-[600px] mx-auto">
                Postępuj zgodnie z tymi krokami, aby Twoje zakupy na Kakobuy były szybkie, bezpieczne i udane
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {guideSteps.map((step, index) => (
                <div
                  key={index}
                  className="relative group p-6 md:p-7 bg-background-100 rounded-lg border border-background-200/70 hover:border-primary-300 transition-all duration-200"
                >
                  <div className="flex items-start gap-4">
                    <div className="font-heading text-4xl font-bold text-primary-200 group-hover:text-primary-300 transition-colors leading-none">
                      {step.step}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 mb-3">
                        <i className={`${step.icon} text-lg`}></i>
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">
                        {step.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {step.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-16 bg-background-100">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-10 text-center">
              Najlepsze praktyki zakupowe
            </h2>

            <div className="space-y-4">
              {tips.map((tip, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-5 md:p-6 bg-background-50 rounded-lg border border-background-200/70"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-lightbulb-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1">
                      {tip.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {tip.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-16 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-10 text-center">
              Powiązane artykuły
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {articles.slice(0, 6).map((article) => (
                <Link
                  key={article.id}
                  to={`/artykul/${article.slug}`}
                  className="group p-5 md:p-6 bg-background-100 rounded-lg border border-background-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer"
                >
                  <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3">
                    <span>{article.date}</span>
                    <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                    <span>{article.readTime}</span>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors leading-snug line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-4">
                    {article.excerpt}
                  </p>
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-primary-600">
                    Czytaj więcej
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-16 bg-background-100">
          <div className="max-w-[800px] mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
              Zacznij zakupy z Kakobuy już dziś
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-[560px] mx-auto mb-8">
              Wykorzystaj wiedzę z poradnika i znajdź najlepsze produkty w Kakobuy Spreadsheet
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 rounded-md text-base font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Otwórz Kakobuy Spreadsheet
                <i className="ri-arrow-right-line"></i>
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50 text-foreground-800 rounded-md text-base font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Strona główna
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}